<template>
    <div class="p-3 d-flex flex-column" style="overflow: hidden">

        <assets-filter-component />

        <div
            class="scroll flex-grow-1"
            ref="personsList"
        >
            <div
                class="task person-list-item"
                v-for="filteredPerson in filteredPersons.data"
                :key="filteredPerson.id"
                :draggable="true"
                @dragstart="dragStart($event, filteredPerson.id)"
            >
                <div
                    class="py-2 task__title"
                    :style="personStyle(filteredPerson)"
                >
                    <p
                        class="m-0 font-weight-bold d-flex align-items-start"
                        style="line-height: 18px"
                    >
                        <span
                            v-if="filteredPerson.latest_badge_time !== undefined"
                            class="status-dot mr-1 mt-1"
                            :class="
                                filteredPerson.latest_badge_time?.type === 'in' ? 'green' : 'red'
                            "
                        ></span>
                        <span v-if="filteredPerson.first_name">
                            {{ filteredPerson.first_name + ' ' }}
                        </span>
                        <span>{{ filteredPerson.last_name }}</span>
                    </p>
                </div>
                <div v-if="filteredPerson.remaining_time_formatted" class="text-center">
                    <div class="custom-progress">
                        <div 
                            class="custom-progress-bar"
                            :style="{ width: `${(filteredPerson.slot_time / filteredPerson.working_time * 100) || 0}%` }"
                        ></div>
                        <span class="custom-progress-text">
                            {{ filteredPerson.remaining_time_formatted }} / {{ filteredPerson.working_time_formatted }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {emitter} from "../../../plugins/Mitt";
import AssetsFilterComponent from "./filter/AssetsFilterComponent";
import {mapActions, mapState} from "vuex";

export default {
    components: {
        AssetsFilterComponent,
    },
    data() {
        return {
            canLoadMore: true,
            filteredPersons: {
                total: 0,
                per_page: 100,
                from: 1,
                to: 0,
                current_page: 1,
                data: [],
            },
            loading: false,
            scrollListenerAttached: false,
        };
    },
    computed:{
        ...mapState('planningFilter', [
            'filter',
            'sort',
        ]),
    },
    methods: {
        getFilteredPersons() {
            if ( (this.loading && this.filteredPersons.current_page === 1) || !this.canLoadMore ) return;
            this.loading = true;
            const url = route('persons.filtered-persons');

            const params = {
                page: this.filteredPersons.current_page,
                filter: this.filter,
                sort: this.sort,
            };
            axios
                .get(url, {
                    params,
                })
                .then((response) => {
                    const newPersonsData = response?.data?.filteredPersons;
                    if (!newPersonsData || !Array.isArray(newPersonsData.data)) {
                        this.canLoadMore = false; // Stop loading if structure is wrong
                        if (this.filteredPersons.current_page === 1) this.filteredPersons.data = [];
                        return;
                    }

                    const newItemsCount = newPersonsData.data.length;

                    if (newPersonsData.current_page > 1) {
                        this.filteredPersons.data = this.filteredPersons.data.concat(
                            newPersonsData.data,
                        );
                    } else {
                        // Replace data, but merge other pagination properties
                        this.filteredPersons = newPersonsData;
                    }

                    this.filteredPersons.current_page = newPersonsData.current_page;

                    this.canLoadMore = newItemsCount === this.filteredPersons.per_page && newItemsCount > 0;
                })
                .catch(error => { // *** ADD/ENSURE CATCH BLOCK ***
                    this.canLoadMore = false; // Stop loading on error
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        handleScroll() {
            const listElm = this.$refs.personsList;

            if (!listElm || this.loading || !this.canLoadMore) return;

            const currentScroll = listElm.scrollTop;
            const clientHeight = listElm.clientHeight;
            const scrollHeight = listElm.scrollHeight;
            const buffer = 100; // Your buffer
            const triggerPoint = scrollHeight - buffer;
            const currentPosition = currentScroll + clientHeight;

            if (currentPosition >= triggerPoint) {
                // Log when the condition is met *before* fetching

                this.filteredPersons.current_page += 1;
                this.getFilteredPersons();
            }
        },
        personsListControlPanelScroller() {
            this.$nextTick(() => {
                const listElm = this.$refs.personsList;

                if (listElm && !this.scrollListenerAttached) {
                    listElm.addEventListener('scroll', this.handleScroll);
                    this.scrollListenerAttached = true;
                } else if (listElm && this.scrollListenerAttached) {
                }
            });
        },
        removeScrollListener() {
            const listElm = this.$refs.personsList;
            if (listElm && this.scrollListenerAttached) {
                listElm.removeEventListener('scroll', this.handleScroll);
                this.scrollListenerAttached = false;
            }
        },

        dragStart(event, personId) {
            //remove draginfo
            this.$store.commit('planning/resetDragStartInfo');
            event.dataTransfer.setData('personId', personId);
        },
        personStyle: function (person) {
            return {
                'background-color': person.status.color,
                'color': person.status.text_color,
            }
        }
    },
    mounted() {
        this.getFilteredPersons();
        this.personsListControlPanelScroller();

        emitter.on('planning-slots-changed', () => {
            console.log('planning-slots-changed');
            this.filteredPersons.current_page = 1;
            this.canLoadMore = true;
            this.getFilteredPersons();
        });
        emitter.on('planning-drop-handled', () => {
            console.log('planning-drop-handled');
            this.filteredPersons.current_page = 1;
            this.canLoadMore = true;
            this.getFilteredPersons();
        });
        emitter.on('slot-deleted', () => {
            console.log('slot-deleted');
            this.filteredPersons.current_page = 1;
            this.canLoadMore = true;
            this.getFilteredPersons();
        });
    },
    beforeUnmount() {
        this.removeScrollListener();
    },
    watch: {
        filter: {
            handler() {
                this.canLoadMore = true;
                this.filteredPersons.current_page = 1;
                this.filteredPersons.data = [];

                this.getFilteredPersons();
            },
            deep: true,
        },
        sort: {
            handler() {
                this.canLoadMore = true;
                this.filteredPersons.current_page = 1;
                this.filteredPersons.data = [];

                this.getFilteredPersons();
            },
            deep: true,
        },
    }
};
</script>

<style>
.scroll {
    overflow-y: auto;
    overflow-x: hidden;
}

.person-list-item{
    display: block;
}

.custom-progress {
    background-color: #999;
    height: 15px;
    position: relative;
    overflow: hidden;
}

.custom-progress-bar {
    background-color: #28a745;
    height: 100%;
    transition: width 0.3s ease;
    position: absolute;
    top: 0;
    left: 0;
}

.custom-progress-text {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
    z-index: 1;
}
</style>
